"use client"

import { router } from "@inertiajs/react"
import { Head } from "@inertiajs/react"
import { Check, Copy01 } from "@untitledui/icons"
import { differenceInCalendarDays, parseISO } from "date-fns"

import { PageHeader } from "@/components/console/page-header"
import { Button } from "@/components/ui/button"
import { PaginationCardMinimal } from "@/components/ui/pagination"
import { Table, TableCard } from "@/components/ui/table"
import { TabList, Tabs } from "@/components/ui/tabs"
import { useClipboard } from "@/hooks/use-clipboard"
import { DashboardLayout } from "@/layouts/dashboard-layout"

interface Booking {
  id: number
  token: string
  account_name: string
  program_type: string
  program_date: string
  program_start_time: string | null
  status: string
  year_level: string | null
  expected_size: number | null
  assignee_name: string | null
  unassigned_reason: string | null
}

interface Pagination {
  page: number
  per_page: number
  total: number
  total_pages: number
}

interface Props {
  stage: string
  assignee?: string
  bookings: Booking[]
  pagination: Pagination
}

const tabs = [
  { id: "pending", label: "Pending" },
  { id: "completed", label: "Completed" },
]

const assigneeTabs = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mine" },
  { id: "unassigned", label: "Unassigned" },
]

/** A group head between rows, or a booking. The queue is grouped before it is coloured. */
type Item =
  | {
      kind: "group"
      id: string
      label: string
      count: number
      tier: "caution" | "quiet"
    }
  | { kind: "booking"; id: number; booking: Booking; daysLate: number }

/** Whole days since the program date; positive once the day has passed. */
const daysLate = (booking: Booking) =>
  differenceInCalendarDays(new Date(), parseISO(booking.program_date))

export default function Dashboard({
  stage,
  assignee = "all",
  bookings,
  pagination,
}: Props) {
  const [copiedText, copy] = useClipboard()
  const completed = stage === "completed"

  const visit = (params: Record<string, string | number>) => {
    router.visit("/dashboard", { data: params, preserveState: true })
  }

  const toItem = (booking: Booking): Item => ({
    kind: "booking",
    id: booking.id,
    booking,
    daysLate: completed ? 0 : daysLate(booking),
  })

  const overdue = completed
    ? []
    : bookings.filter((b) => daysLate(b) > 0).map(toItem)
  const rest = completed
    ? bookings.map(toItem)
    : bookings.filter((b) => daysLate(b) <= 0).map(toItem)

  const items: Item[] = completed
    ? rest
    : [
        ...(overdue.length > 0
          ? [
              {
                kind: "group" as const,
                id: "g-overdue",
                label: "overdue",
                count: overdue.length,
                tier: "caution" as const,
              },
              ...overdue,
            ]
          : []),
        ...(rest.length > 0
          ? [
              {
                kind: "group" as const,
                id: "g-upcoming",
                label: "upcoming",
                count: rest.length,
                tier: "quiet" as const,
              },
              ...rest,
            ]
          : []),
      ]

  // school · program · assignee · date · start time · year level · cohort size (+ actions when pending)
  const COLUMNS = completed ? 7 : 8

  const formHref = (token: string) => `/forms/checkins/${token}`
  const rowHref = (token: string) =>
    completed ? `/dashboard/checkins/${token}` : formHref(token)

  return (
    <DashboardLayout>
      <Head title="Check-ins" />
      <div className="max-w-container mx-auto mb-8 flex flex-col gap-5 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "forms" }, { label: "check-ins" }]}
          title="Check-ins"
          description="Manage program check-in forms."
        />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <Tabs
            orientation="horizontal"
            selectedKey={stage}
            onSelectionChange={(key) =>
              visit({ stage: String(key), assignee, page: 1 })
            }
          >
            <TabList size="sm" type="underline" items={tabs} />
          </Tabs>
          <Tabs
            orientation="horizontal"
            selectedKey={assignee}
            onSelectionChange={(key) =>
              visit({ stage, assignee: String(key), page: 1 })
            }
          >
            <TabList size="sm" type="underline" items={assigneeTabs} />
          </Tabs>
        </div>
      </div>
      <div className="max-w-container mx-auto flex flex-col px-4 lg:gap-6 lg:px-8">
        <TableCard.Root bleed>
          <TableCard.Caption
            title={completed ? "Completed" : "Pending"}
            count={`${pagination.total} check-ins`}
            alert={
              !completed && overdue.length > 0
                ? `${overdue.length} overdue`
                : undefined
            }
            note="date ↓"
          />
          <Table aria-label="Check-ins">
            <Table.Header>
              <Table.Head
                id="school"
                isRowHeader
                label="School"
                className="w-full"
              />
              <Table.Head id="program" label="Program" />
              <Table.Head id="assignee" label="Assignee" />
              <Table.Head id="date" label="Date" align="right" sorted="desc" />
              <Table.Head id="time" label="Start time" />
              <Table.Head id="year" label="Year level" />
              <Table.Head id="size" label="Cohort size" align="right" />
              {!completed && (
                <Table.Head
                  id="actions"
                  label=""
                  className="w-ledger-actions"
                />
              )}
            </Table.Header>
            <Table.Body items={items}>
              {(item) =>
                item.kind === "group" ? (
                  <Table.Group
                    id={item.id}
                    label={item.label}
                    count={item.count}
                    tier={item.tier}
                    span={COLUMNS}
                  />
                ) : (
                  <Table.Row
                    id={item.id}
                    href={rowHref(item.booking.token)}
                    tone={item.daysLate > 0 ? "caution" : undefined}
                  >
                    <Table.Lead>{item.booking.account_name}</Table.Lead>
                    <Table.Cell className="text-secondary">
                      {item.booking.program_type}
                    </Table.Cell>
                    {item.booking.unassigned_reason ? (
                      <Table.Status tier="caution">
                        {item.booking.unassigned_reason}
                      </Table.Status>
                    ) : (
                      <Table.Cell className="whitespace-nowrap">
                        {item.booking.assignee_name || "—"}
                      </Table.Cell>
                    )}
                    <Table.When
                      at={item.booking.program_date}
                      pattern="d MMM yyyy"
                      chip={
                        item.daysLate > 0 ? (
                          <Table.Chip tier="caution">
                            +{item.daysLate}d
                          </Table.Chip>
                        ) : undefined
                      }
                    />
                    <Table.Id>{item.booking.program_start_time}</Table.Id>
                    <Table.Cell>{item.booking.year_level || "—"}</Table.Cell>
                    <Table.Figure>{item.booking.expected_size}</Table.Figure>
                    {!completed && (
                      <Table.Actions>
                        <Button
                          size="sm"
                          color="tertiary"
                          iconLeading={
                            copiedText ===
                            `${window.location.origin}${formHref(item.booking.token)}`
                              ? Check
                              : Copy01
                          }
                          onPress={() =>
                            copy(
                              `${window.location.origin}${formHref(item.booking.token)}`,
                            )
                          }
                          aria-label="Copy check-in link"
                        />
                      </Table.Actions>
                    )}
                  </Table.Row>
                )
              }
            </Table.Body>
          </Table>
          <TableCard.Footer
            shown={bookings.length}
            total={pagination.total}
            trailing={
              <PaginationCardMinimal
                align="right"
                page={pagination.page}
                total={pagination.total_pages}
                onPageChange={(p) => visit({ stage, assignee, page: p })}
                className="border-0 p-0 md:p-0"
              />
            }
          />
        </TableCard.Root>
      </div>
    </DashboardLayout>
  )
}
